/**
 * 멤버 탭에서 실제로 **무언가를 바꾸는** 조각들. 표 자체는 서버에서 그린다.
 *
 * 메일 발송이 없으므로 초대는 「링크를 만들어 건네는 일」이다 —
 * 다이얼로그가 닫히면 링크를 다시 볼 길이 없으면 곤란하니, 만든 뒤에도
 * 칸에 남겨 두고 표에서도 다시 복사할 수 있게 한다.
 */

import { Copy, UserPlus } from "lucide-react";
import { FormEvent, useState, useTransition } from "react";
import { toast } from "sonner";

import { Button } from "@buildpy/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@buildpy/ui/dialog";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import { SimpleSelect } from "@buildpy/ui/select";
import { settleRedirect } from "@/lib/redirect-error";
import { INVITE_DAYS } from "@buildpy/core/modules/org/invite-view";
import { createInvite, revokeInvite } from "@/api/actions/org";
import { MEMBER_ROLES, MemberRole, ROLE_LABEL } from "@buildpy/core/modules/org/types";

/** 상대 경로 → 지금 보고 있는 도메인 기준 절대 주소. 서버는 도메인을 모른다. */
const absolute = (path: string): string =>
  typeof window === "undefined"
    ? path
    : new URL(path, window.location.origin).toString();

/**
 * 클립보드는 https·사용자 제스처 안에서만 열린다 — 막히면 조용히 실패하지 말고
 * 「직접 복사하세요」 라고 말한다. 링크는 칸에 그대로 남아 있다.
 */
const copy = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("링크를 복사했습니다");
  } catch {
    toast.error("복사하지 못했습니다. 칸에서 직접 복사하세요.");
  }
};

/** 초대 다이얼로그. 성공하면 링크를 같은 자리에 띄운다. */
export const InviteButton = () => {
  const [open, setOpen] = useState(false);
  const [pending, startTransition] = useTransition();
  const [link, setLink] = useState<string | null>(null);
  const [invited, setInvited] = useState<string>("");

  const reset = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setLink(null);
      setInvited("");
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") ?? "");
    const role = String(form.get("role") ?? "staff") as MemberRole;

    startTransition(async () => {
      try {
        const result = await createInvite({ email, role });
        if (result.ok) {
          setLink(absolute(result.path));
          setInvited(result.email);
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      } catch (caught) {
        // 세션이 끊기면(401) settleRedirect 가 세션을 지우고 /login 으로 옮긴다 — 토스트로 삼키지 않는다.
        if (settleRedirect(caught)) return;
        toast.error("초대하지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  };

  return (
    <>
      <Button onClick={() => reset(true)} size="sm" type="button">
        <UserPlus className="size-4" />
        초대
      </Button>

      <Dialog onOpenChange={reset} open={open}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>멤버 초대</DialogTitle>
            <DialogDescription>
              메일은 보내지 않습니다. 만들어진 링크를 복사해 직접 전하세요.
            </DialogDescription>
          </DialogHeader>

          {link ? (
            <div className="flex flex-col gap-y-3">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{invited}</span>{" "}
                초대 링크입니다. {INVITE_DAYS}일 뒤 만료됩니다.
              </p>
              <div className="flex items-center gap-2">
                <Input
                  name="invite-link"
                  aria-label="초대 링크"
                  onFocus={(event) => event.currentTarget.select()}
                  readOnly
                  value={link}
                />
                <Button
                  onClick={() => void copy(link)}
                  size="sm"
                  type="button"
                  variant="outline">
                  <Copy className="size-4" />
                  복사
                </Button>
              </div>
              <DialogFooter>
                <Button onClick={() => reset(false)} size="sm" type="button">
                  닫기
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form className="flex flex-col gap-y-4" onSubmit={onSubmit}>
              <div className="flex flex-col gap-y-1.5">
                <Label htmlFor="invite-email">이메일</Label>
                <Input
                  autoComplete="off"
                  disabled={pending}
                  id="invite-email"
                  name="email"
                  placeholder="sokjang@example.com"
                  required
                  type="email"
                />
              </div>

              <div className="flex flex-col gap-y-1.5">
                <Label htmlFor="invite-role">권한</Label>
                <SimpleSelect
                  defaultValue="staff"
                  disabled={pending}
                  id="invite-role"
                  name="role"
                  options={MEMBER_ROLES.map((role) => ({ value: role, label: ROLE_LABEL[role] }))}
                />
              </div>

              <DialogFooter>
                <Button disabled={pending} size="sm" type="submit">
                  {pending ? "만드는 중…" : "초대 링크 만들기"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

interface RowProps {
  inviteId: string;
  /** 살아 있는 초대만 링크를 준다. 죽은 링크를 복사해 보내는 일이 없게. */
  path: string | null;
}

/** 미수락 초대 한 줄의 버튼들. 대표에게만 그려진다. */
export const InviteRowActions = ({ inviteId, path }: RowProps) => {
  const [pending, startTransition] = useTransition();

  const onRevoke = () => {
    startTransition(async () => {
      try {
        const result = await revokeInvite(inviteId);
        if (result.ok) toast.success(result.message);
        else toast.error(result.message);
      } catch (caught) {
        if (settleRedirect(caught)) return;
        toast.error("회수하지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  };

  return (
    <div className="flex items-center justify-end gap-1.5">
      {path && (
        <Button
          onClick={() => void copy(absolute(path))}
          size="sm"
          type="button"
          variant="outline">
          <Copy className="size-4" />
          링크 복사
        </Button>
      )}
      {path && (
        <Button
          disabled={pending}
          onClick={onRevoke}
          size="sm"
          type="button"
          variant="destructive">
          회수
        </Button>
      )}
    </div>
  );
};
